import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { PROGETTI } from "../lib/lavori";
import { Check, X, Star, MapPin, PenLine, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = () => (
  <div className="flex gap-1" data-testid="project-stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={14} className="fill-honey text-honey" />
    ))}
  </div>
);

const ReviewInvite = ({ progetto }) => {
  const [openForm, setOpenForm] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ autore: "", indirizzo: "", testo: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const inputCls =
    "w-full rounded-xl border border-cream/15 bg-walnut/70 px-4 py-2.5 font-sans text-sm text-cream placeholder:text-sand/40 outline-none transition-[border-color] duration-300 focus:border-honey";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/recensioni`, {
        progetto_id: progetto.id,
        progetto: `${progetto.cliente} — ${progetto.zona}`,
        ...form,
      });
      setSent(true);
      toast.success("Recensione inviata! La pubblicheremo dopo una breve verifica.");
    } catch {
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mt-10 rounded-2xl border border-dashed border-honey/40 bg-bark/40 p-6"
      data-testid="review-invite-box"
    >
      <p className="flex items-center gap-2 font-display text-xl italic text-cream">
        <PenLine size={18} className="text-honey" /> È casa tua?
      </p>
      <p className="mt-2 font-sans text-xs font-light leading-relaxed text-sand/75">
        Inserisci l'indirizzo dell'immobile e scrivi la tua recensione: la
        pubblicheremo qui dopo aver verificato che sei il vero proprietario.
      </p>
      {sent ? (
        <p className="mt-4 font-sans text-sm text-honey" data-testid="review-sent-msg">
          Grazie! Recensione ricevuta, in attesa di verifica.
        </p>
      ) : openForm ? (
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3" data-testid="review-form">
          <input required value={form.autore} onChange={set("autore")} placeholder="Il tuo nome" className={inputCls} data-testid="review-autore-input" />
          <input required value={form.indirizzo} onChange={set("indirizzo")} placeholder="Indirizzo dell'immobile" className={inputCls} data-testid="review-indirizzo-input" />
          <textarea required rows={3} value={form.testo} onChange={set("testo")} placeholder="Come ti sei trovato con il nostro lavoro?" className={`${inputCls} resize-none`} data-testid="review-testo-input" />
          <button type="submit" disabled={sending} className="flex items-center justify-center gap-2 rounded-full bg-honey py-2.5 font-sans text-sm font-medium text-ink transition-colors hover:bg-cream disabled:opacity-60" data-testid="review-submit-button">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Invio..." : "Invia la recensione"}
          </button>
        </form>
      ) : (
        <button onClick={() => setOpenForm(true)} className="mt-4 rounded-full border border-honey/50 px-5 py-2 font-sans text-xs font-medium text-honey transition-colors hover:bg-honey hover:text-ink" data-testid="review-open-form-button">
          Scrivi la tua recensione
        </button>
      )}
    </div>
  );
};

const Lavori = () => {
  const [open, setOpen] = useState(null);
  const progetti = [...PROGETTI].sort(
    (a, b) => (a.prio ?? 99) - (b.prio ?? 99)
  );

  return (
    <section
      id="lavori"
      className="grain relative bg-walnut py-28 text-cream md:py-40"
      data-testid="library-section"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="mb-4 font-sans text-xs font-light uppercase tracking-[0.45em] text-honey">
              La libreria dei lavori
            </p>
            <h2 className="font-display text-4xl font-medium leading-tight sm:text-5xl lg:text-6xl">
              Ogni casa, <em className="text-honey">una storia</em>
            </h2>
          </div>
          <p className="max-w-xs pb-2 font-sans text-xs font-light leading-relaxed text-sand/70">
            Nessuna promessa: apri una scheda e guarda i lavori eseguiti,
            cantiere per cantiere. Il resto lo giudichi da solo.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {progetti.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <button
                onClick={() => setOpen(p)}
                data-testid={`project-cover-${p.id}`}
                className="group block w-full text-left"
              >
                <div className="relative overflow-hidden rounded-xl border border-cream/10">
                  <img
                    src={p.cover}
                    alt={`Lavoro di posa parquet per ${p.cliente}, ${p.zona}`}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-walnut/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="absolute bottom-3 left-4 translate-y-2 font-sans text-[9px] uppercase tracking-[0.35em] text-cream opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    Apri il progetto
                  </span>
                </div>
                <div className="mt-3 flex items-baseline justify-between gap-2">
                  <span className="font-display text-xl font-medium text-cream transition-colors duration-300 group-hover:text-honey">
                    {p.cliente}
                  </span>
                  <span className="flex items-center gap-1.5 text-right font-sans text-[10px] font-light uppercase tracking-[0.15em] text-sand/70">
                    <MapPin size={11} className="shrink-0 text-honey" />
                    {p.zona}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-md md:p-8"
            data-testid="project-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="grain relative grid max-h-[88vh] w-full max-w-6xl gap-10 overflow-y-auto rounded-3xl border border-cream/10 bg-walnut p-7 md:p-12 lg:grid-cols-[1fr_1.15fr]"
            >
              <button
                onClick={() => setOpen(null)}
                data-testid="project-close-button"
                aria-label="Chiudi"
                className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-sand transition-colors duration-300 hover:border-honey hover:text-honey"
              >
                <X size={18} />
              </button>

              <div>
                <p className="flex items-center gap-1.5 font-sans text-xs font-light uppercase tracking-[0.35em] text-honey">
                  <MapPin size={12} /> {open.zona}
                </p>
                <h3 className="mt-3 font-display text-4xl font-medium text-cream md:text-5xl">
                  {open.cliente}
                </h3>

                <p className="mb-4 mt-10 font-sans text-xs font-light uppercase tracking-[0.35em] text-sand/60">
                  Lavori eseguiti
                </p>
                <ul
                  className="flex flex-col gap-3"
                  data-testid="project-works-list"
                >
                  {open.lavori.map((l) => (
                    <li
                      key={l}
                      className="flex items-start gap-3 font-sans text-sm font-light text-sand/90"
                    >
                      <Check size={16} className="mt-0.5 shrink-0 text-honey" />
                      {l}
                    </li>
                  ))}
                </ul>

                {open.recensione ? (
                  <div
                    className="mt-10 rounded-2xl border border-honey/25 bg-bark/60 p-6"
                    data-testid="project-review"
                  >
                    <Stars />
                    <p className="mt-4 font-display text-xl italic leading-relaxed text-cream">
                      “{open.recensione}”
                    </p>
                    <p className="mt-4 font-sans text-xs font-light uppercase tracking-[0.25em] text-sand/60">
                      {open.recensore || `${open.cliente} · ${open.zona}`}
                    </p>
                  </div>
                ) : (
                  <ReviewInvite progetto={open} />
                )}
              </div>

              <div className="grid content-start gap-4 sm:grid-cols-2">
                {(open.videos ||
                  (open.video
                    ? [{ src: open.video, poster: open.poster }]
                    : [])
                ).map((v, vi) => (
                  <div
                    key={v.src}
                    className="relative sm:col-span-2"
                    data-testid={`project-video-${vi}`}
                  >
                    <motion.video
                      src={v.src}
                      poster={v.poster || open.foto[0]}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + vi * 0.1, duration: 0.5 }}
                      className="aspect-video w-full rounded-xl object-cover"
                    />
                    {v.label && (
                      <span className="absolute left-4 top-4 rounded-full bg-ink/70 px-4 py-1.5 font-sans text-[10px] uppercase tracking-[0.3em] text-cream backdrop-blur-sm">
                        {v.label}
                      </span>
                    )}
                  </div>
                ))}
                {open.foto.map((f, i) => (
                  <motion.img
                    key={f}
                    src={f}
                    alt={`Foto ${i + 1} del lavoro per ${open.cliente}`}
                    loading="lazy"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
                    className={`w-full rounded-xl object-cover ${
                      i === 0 && !open.video && !open.videos
                        ? "aspect-[16/10] sm:col-span-2"
                        : "aspect-[4/3]"
                    }`}
                    data-testid={`project-photo-${i}`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Lavori;
